import {
  getUserIdFromRefreshToken,
  HTTPStatus,
  isLoggedIn,
  ServiceError,
} from "@shapr3d/frontend-lib";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { queryKeys } from "../config";
import { UMSrequestClient } from "../stores/requestClient";
import { ServiceErrorCode } from "../types";

export type AppProperties = {
  is_mad?: boolean;
  survey_is_enterprise_potential?: boolean;
  segmentation_is_student_or_teacher?: boolean;
  segmentation_v2_finished_at?: string;
  segmentation_v2_progressive_profiling_finished_at?: string;
  segmentation_work_web_completed?: boolean;
  segmentation_work_completed?: boolean;
  segmentation_v2_download_intent?: string;
  segmentation_v2_use_at_work?: string;
  segmentation_v2_company_design?: string;
  is_resegmentation_needed?: boolean;
  web_feature_tour_project_sharing_collaborator_completed?: boolean;
  web_feature_tour_project_sharing_owner_completed?: boolean;
  segmentation_proficiency?: string;
  segmentation_user_intent?: string;
};

export const appPropertiesQueryKey = [...queryKeys.authenticatedQueryKey, "app-properties"];

export const useGetAppProperties = () => {
  return useQuery({
    queryKey: appPropertiesQueryKey,
    queryFn: async ({ signal }) => {
      const id = getUserIdFromRefreshToken();
      try {
        const response = await UMSrequestClient.authenticatedRequest({
          method: "GET",
          url: `/users/${id}/app-properties`,
          abortSignal: signal,
        });
        return await response.json<AppProperties>();
      } catch (error) {
        if (error instanceof ServiceError) {
          if (error.hasStatusAndCode(HTTPStatus.NotFound, ServiceErrorCode.AppPropertiesNotFound)) {
            return null;
          }
        }
        throw error;
      }
    },

    enabled: isLoggedIn(),
    staleTime: Infinity,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    placeholderData: keepPreviousData,
  });
};

export const useSetAppProperties = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (appProperties: AppProperties) => {
      const id = getUserIdFromRefreshToken();
      try {
        const response = await UMSrequestClient.authenticatedRequest({
          method: "PUT",
          url: `/users/${id}/app-properties`,
          body: appProperties,
        });
        const responseData = await response.json<AppProperties>();
        queryClient.setQueryData(appPropertiesQueryKey, () => {
          return responseData;
        });
        return responseData;
      } catch (error) {
        if (error instanceof ServiceError) {
          if (error.hasStatusAndCode(HTTPStatus.NotFound, ServiceErrorCode.AppPropertiesNotFound)) {
            return null;
          }
        }
        throw error;
      }
    },
  });
};
